import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { MagnifyingGlass, ArrowsClockwise, MapPin, Car as CarIcon, Clock, User as UserIcon } from "@phosphor-icons/react";

const STATUS_FILL = {
  available:      "#22C55E",
  occupied:       "#EF4444",
  owner_reserved: "#3B82F6",
  leased:         "#EAB308",
  daily_rental:   "#F97316",
  maintenance:    "#71717A",
  reserved:       "#71717A",
  guest:          "#A855F7",
  staff:          "#1E3A8A",
  ev:             "#86EFAC",
};

const STATUS_LABEL = {
  available: "Available",
  occupied: "Occupied",
  owner_reserved: "Owner Reserved",
  leased: "Leased",
  daily_rental: "Daily Rental",
  maintenance: "Maintenance",
  reserved: "Reserved",
  guest: "Guest",
  staff: "Staff",
  ev: "EV",
};

const ZONE_BBOX = {
  // x, y, w, h derived from seed_amaia.ZONE_LAYOUT (v3)
  "Clara":           { x:   30, y:   60, w: 8  * (32 + 4) - 4, h: 6  * (56 + 10) + 28 },
  "Aria":            { x:  380, y:   60, w: 10 * (32 + 4) - 4, h: 6  * (56 + 10) + 28 },
  "Blanca":          { x:  820, y:   60, w: 10 * (32 + 4) - 4, h: 10 * (56 + 10) + 28 },
  "Delicia":         { x:   30, y:  820, w: 12 * (32 + 4) - 4, h: 5  * (56 + 10) + 28 },
  "East Perimeter":  { x:  550, y:  820, w: 17 * (32 + 4) - 4, h: 5  * (56 + 10) + 28 },
  "North Perimeter": { x:   30, y: 1220, w: 19 * (32 + 4) - 4, h: 4  * (56 + 10) + 28 },
};

const ZONE_TINT = {
  "Clara":           "#7C4DFF",
  "Aria":            "#00ACC1",
  "Blanca":          "#43A047",
  "Delicia":         "#FB8C00",
  "East Perimeter":  "#E53935",
  "North Perimeter": "#5E35B1",
};

const POLL_MS = 5000;

function formatDuration(min) {
  if (min == null) return "—";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}

function Counter({ label, value, color = "#1F2933", testId }) {
  return (
    <div
      data-testid={testId}
      className="px-4 py-3 rounded-lg bg-white border border-[#E2D8BE] flex flex-col min-w-[110px]"
    >
      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#8A7B5C]">
        {label}
      </span>
      <span className="text-2xl font-black tracking-tight mt-0.5" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

export default function LiveMap() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const svgRef = useRef(null);
  const wrapperRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const r = await api.get("/live-map/state");
      setState(r.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error("Live map fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchState();
    const t = setInterval(fetchState, POLL_MS);
    return () => clearInterval(t);
  }, [fetchState]);

  const coordsBySlotId = useMemo(() => {
    const m = {};
    (state?.coordinates || []).forEach((c) => { m[c.slot_id] = c; });
    return m;
  }, [state]);

  const slotById = useMemo(() => {
    const m = {};
    (state?.slots || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [state]);

  const canvasW = state?.site_plan?.canvas_width || 1500;
  const canvasH = state?.site_plan?.canvas_height || 1200;

  const selectedSlot = selectedSlotId ? slotById[selectedSlotId] : null;
  const selectedCoord = selectedSlotId ? coordsBySlotId[selectedSlotId] : null;

  // Search: find by plate or slot number, then highlight and scroll into view
  const onSearch = () => {
    const q = search.trim().toUpperCase();
    if (!q) return;
    const slots = state?.slots || [];
    // Try slot_number exact / startswith
    let found =
      slots.find((s) => s.slot_number === q) ||
      slots.find((s) => s.active_plate && s.active_plate.toUpperCase() === q) ||
      slots.find((s) => s.slot_number.startsWith(q)) ||
      slots.find((s) => s.active_plate && s.active_plate.toUpperCase().includes(q));
    if (!found) return;
    setHighlightId(found.id);
    setSelectedSlotId(found.id);
    // Scroll the highlighted slot into view inside the SVG container
    const coord = coordsBySlotId[found.id];
    if (coord && wrapperRef.current && svgRef.current) {
      const scaleX = svgRef.current.clientWidth / canvasW;
      const scaleY = svgRef.current.clientHeight / canvasH;
      const elX = coord.x * scaleX;
      const elY = coord.y * scaleY;
      wrapperRef.current.scrollTo({
        left: Math.max(0, elX - wrapperRef.current.clientWidth / 2),
        top: Math.max(0, elY - wrapperRef.current.clientHeight / 2),
        behavior: "smooth",
      });
    }
    // Clear highlight after a few seconds
    setTimeout(() => setHighlightId(null), 4000);
  };

  const counters = state?.counters || {
    total: 0, occupied: 0, available: 0, guest: 0, reserved: 0, leased: 0,
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8A7B5C]">
            Live Operations Map
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-1 text-[#1F2933]">
            Amaia Steps · Real-time view
          </h1>
          <p className="text-xs text-[#6B7280] mt-1 font-mono">
            {lastUpdated ? `Last refreshed ${lastUpdated.toLocaleTimeString()}` : loading ? "Loading…" : ""}
            {" · auto-polling every 5s"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="live-map-refresh-button"
          onClick={fetchState}
          className="border-[#E2D8BE] text-[#1F2933] hover:bg-[#F1EFE6]"
        >
          <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
      </header>

      {/* Counters */}
      <div className="flex flex-wrap gap-2" data-testid="live-map-counters">
        <Counter label="Total" value={counters.total} testId="counter-total" />
        <Counter label="Available" value={counters.available} color="#22C55E" testId="counter-available" />
        <Counter label="Occupied" value={counters.occupied} color="#EF4444" testId="counter-occupied" />
        <Counter label="Guest" value={counters.guest} color="#A855F7" testId="counter-guest" />
        <Counter label="Owner" value={counters.owner_reserved} color="#3B82F6" testId="counter-owner" />
        <Counter label="Leased" value={counters.leased} color="#EAB308" testId="counter-leased" />
        <Counter label="Reserved" value={counters.reserved} color="#71717A" testId="counter-reserved" />
        <Counter label="Maint." value={counters.maintenance} color="#71717A" testId="counter-maintenance" />
      </div>

      {/* Search + legend */}
      <Card className="bg-white border-[#E2D8BE] p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={18} className="text-[#8A7B5C]" />
          <Input
            data-testid="live-map-search-input"
            placeholder="Search slot # (e.g. 027) or plate (e.g. ABC-1234)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
            className="bg-white border-[#E2D8BE]"
          />
          <Button
            size="sm"
            data-testid="live-map-search-button"
            onClick={onSearch}
            className="bg-[#1F2933] text-white hover:bg-[#374151]"
          >
            Find
          </Button>
        </div>
        <div className="flex flex-wrap gap-3 ml-auto">
          {Object.entries(STATUS_LABEL).map(([k, l]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-sm border border-black/20" style={{ background: STATUS_FILL[k] }} />
              <span className="text-[11px] font-mono text-[#374151]">{l}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Map canvas */}
      <Card className="bg-white border-[#E2D8BE] p-2 overflow-hidden">
        <div
          ref={wrapperRef}
          className="relative w-full overflow-auto bg-[#FBFAF4] rounded-md border border-[#EDE6CF]"
          style={{ maxHeight: "72vh" }}
          data-testid="live-map-canvas-wrapper"
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${canvasW} ${canvasH}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto block"
            style={{ minWidth: 900 }}
            data-testid="live-map-svg"
          >
            {/* Site plan image */}
            {state?.site_plan?.image_base64 && (
              <image
                href={state.site_plan.image_base64.startsWith("data:")
                  ? state.site_plan.image_base64
                  : `data:${state.site_plan.mime_type || "image/png"};base64,${state.site_plan.image_base64}`}
                x="0" y="0" width={canvasW} height={canvasH} opacity="0.45"
              />
            )}

            {/* Zone outlines + labels */}
            {Object.entries(ZONE_BBOX).map(([name, b]) => (
              <g key={name}>
                <rect
                  x={b.x - 8} y={b.y - 8} width={b.w + 16} height={b.h + 16}
                  fill={ZONE_TINT[name]} fillOpacity="0.04"
                  stroke={ZONE_TINT[name]} strokeOpacity="0.35" strokeDasharray="6 4" strokeWidth="1.5"
                  rx="10"
                />
                <text
                  x={b.x - 4} y={b.y + 4}
                  fontSize="14" fontWeight="700"
                  fontFamily="ui-monospace, SFMono-Regular, monospace"
                  fill={ZONE_TINT[name]} opacity="0.85"
                >
                  {name.toUpperCase()}
                </text>
              </g>
            ))}

            {/* Slots */}
            {(state?.slots || []).map((s) => {
              const c = coordsBySlotId[s.id];
              if (!c) return null;
              const fill = STATUS_FILL[s.status] || "#9CA3AF";
              const isSel = s.id === selectedSlotId;
              const isHi = s.id === highlightId;
              const stroke = isSel ? "#0F172A" : isHi ? "#F59E0B" : "rgba(0,0,0,0.25)";
              const strokeW = isSel || isHi ? 2.5 : 0.8;
              return (
                <g
                  key={s.id}
                  data-testid={`slot-${s.slot_number}`}
                  transform={`translate(${c.x},${c.y}) rotate(${c.rotation || 0})`}
                  onClick={() => setSelectedSlotId(s.id)}
                  style={{ cursor: "pointer" }}
                >
                  <rect
                    width={c.width} height={c.height} rx="4"
                    fill={fill} stroke={stroke} strokeWidth={strokeW}
                  />
                  {isHi && (
                    <rect
                      x={-4} y={-4} width={c.width + 8} height={c.height + 8} rx="8"
                      fill="none" stroke="#F59E0B" strokeWidth="3"
                      style={{ animation: "pulse 1.2s infinite" }}
                    />
                  )}
                  <text
                    x={c.width / 2} y={c.height / 2 + 4}
                    textAnchor="middle"
                    fontSize="12" fontWeight="700"
                    fontFamily="ui-monospace, SFMono-Regular, monospace"
                    fill={["available", "ev"].includes(s.status) ? "#0F172A" : "#FFFFFF"}
                    pointerEvents="none"
                  >
                    {s.slot_number}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </Card>

      {/* Slot details drawer */}
      <Sheet open={!!selectedSlotId} onOpenChange={(o) => !o && setSelectedSlotId(null)}>
        <SheetContent side="right" className="w-[420px] sm:max-w-[420px] bg-white" data-testid="slot-details-drawer">
          {selectedSlot && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-3xl font-black tracking-tighter text-[#1F2933]">
                  Slot {selectedSlot.slot_number}
                </SheetTitle>
                <SheetDescription>
                  <span
                    className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold border"
                    style={{
                      background: STATUS_FILL[selectedSlot.status] + "22",
                      color: STATUS_FILL[selectedSlot.status],
                      borderColor: STATUS_FILL[selectedSlot.status] + "55",
                    }}
                    data-testid="slot-detail-status"
                  >
                    {STATUS_LABEL[selectedSlot.status] || selectedSlot.status}
                  </span>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-4">
                <DetailRow icon={<MapPin size={16} />} label="Zone">
                  {selectedCoord?.zone_name || "—"}
                </DetailRow>
                <DetailRow label="Slot Type">
                  <span className="capitalize">{selectedSlot.slot_type?.replace("_", " ")}</span>
                </DetailRow>
                <DetailRow icon={<UserIcon size={16} />} label="Owner">
                  {selectedSlot.owner_name
                    ? `${selectedSlot.owner_name}${selectedSlot.owner_unit ? ` · Unit ${selectedSlot.owner_unit}` : ""}`
                    : "—"}
                </DetailRow>
                <DetailRow icon={<UserIcon size={16} />} label="Tenant">
                  {selectedSlot.tenant_name
                    ? `${selectedSlot.tenant_name}${selectedSlot.tenant_unit ? ` · Unit ${selectedSlot.tenant_unit}` : ""}`
                    : "—"}
                </DetailRow>
                <DetailRow icon={<CarIcon size={16} />} label="Vehicle">
                  {[selectedSlot.vehicle_brand, selectedSlot.vehicle_model, selectedSlot.vehicle_color]
                    .filter(Boolean).join(" · ") || "—"}
                </DetailRow>
                <DetailRow label="Active Plate">
                  <span className="font-mono font-semibold" data-testid="slot-detail-plate">
                    {selectedSlot.active_plate || "—"}
                  </span>
                </DetailRow>
                <DetailRow icon={<Clock size={16} />} label="Entry Time">
                  {selectedSlot.entry_time
                    ? new Date(selectedSlot.entry_time).toLocaleString()
                    : "—"}
                </DetailRow>
                <DetailRow icon={<Clock size={16} />} label="Duration">
                  <span data-testid="slot-detail-duration">
                    {formatDuration(selectedSlot.duration_minutes)}
                  </span>
                </DetailRow>
              </div>

              <div className="mt-8 pt-4 border-t border-[#E2D8BE] text-[11px] font-mono text-[#8A7B5C]">
                Slot ID: {selectedSlot.id.slice(0, 8)}…
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 text-[#8A7B5C] pt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#8A7B5C]">{label}</p>
        <p className="text-sm text-[#1F2933] mt-0.5">{children}</p>
      </div>
    </div>
  );
}
